import { CalendarOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { TaskListItem } from './TaskListItem';
import { compareTasksByCompletionThenPriority, type TaskClaimSummary } from '@/lib/task-filters';
import type { ClaimTask } from '@/api/generated/client';

interface NoDueDateTasksCardProps {
  tasks: ClaimTask[];
  claimsMap: Map<number, TaskClaimSummary>;
  onTaskComplete?: (taskId: number, claimId: number) => void;
  onTaskEdit?: (task: ClaimTask) => void;
}

/**
 * Card shown underneath the calendar (calendar view, left column) listing all
 * tasks that have no due date, active or closed (completed/cancelled), with
 * active tasks first and closed ones after, each ordered by priority. These
 * tasks never appear on any calendar day, so without this card they'd be
 * invisible while in calendar view (they're only otherwise visible via the
 * "No Due Date" group in the flat list view).
 */
export function NoDueDateTasksCard({ tasks, claimsMap, onTaskComplete, onTaskEdit }: NoDueDateTasksCardProps) {
  const noDueDateTasks = tasks.filter((task) => !task.dueDate).sort(compareTasksByCompletionThenPriority);

  if (noDueDateTasks.length === 0) {
    return null;
  }

  // Cap the card to ~3 task rows so it never grows with the number of no-due-date
  // tasks; the rest scrolls inside the card. The fixed height is only applied once
  // there are more than 3 tasks so a short list still shrinks to fit instead of
  // leaving empty space. (A fixed height - not max-height - is required because the
  // Radix ScrollArea viewport uses height:100%, which only scrolls against a parent
  // with a definite height.)
  const MAX_VISIBLE_TASKS = 3;
  const isScrollable = noDueDateTasks.length > MAX_VISIBLE_TASKS;

  return (
    <div className="border rounded-lg p-4 bg-card" data-testid="no-due-date-tasks-card">
      <div className="flex items-center gap-2 mb-4">
        <CalendarOff className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">No Due Date</h2>
        <Badge variant="outline" className="text-[10px] h-5 px-1.5">
          {noDueDateTasks.length}
        </Badge>
      </div>
      {/* Cap the visible height to ~3 task rows so the card never grows with the number
          of no-due-date tasks; anything beyond that scrolls inside the card. */}
      <ScrollArea className={cn('pr-4', isScrollable && 'h-[288px]')}>
        <div className="space-y-3">
          {noDueDateTasks.map((task) => (
            <TaskListItem
              key={task.id}
              task={task}
              claim={claimsMap.get(task.claimId)}
              onTaskComplete={onTaskComplete}
              onTaskEdit={onTaskEdit}
              badgeLayout="inline"
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
